import { useCallback } from "react";

interface UseSelectFocusParams {
  selectedValue: string;
  query: string;
  getDisplayNameForValue: (optionValue: string) => string;
  setQuery: (value: string) => void;
  setIsFocused: (value: boolean) => void;
  displayNameRef: React.MutableRefObject<string>;
}

/**
 * Resolves the query text to restore on blur: the cached display name, or the
 * freshly derived display name for the selected value, or `null` when empty.
 *
 * @param displayNameRef Ref caching the last resolved display name.
 * @param selectedValue The currently selected option value.
 * @param getDisplayNameForValue Resolver from option value to display name.
 * @returns The query text to set, or `null` when there is nothing selected.
 */
function resolveBlurQuery(
  displayNameRef: React.MutableRefObject<string>,
  selectedValue: string,
  getDisplayNameForValue: (v: string) => string,
): string | null {
  if (displayNameRef.current) return displayNameRef.current;
  if (!selectedValue) return null;
  const display = getDisplayNameForValue(selectedValue);
  displayNameRef.current = display;
  return display;
}

/**
 * Provides focus/blur handlers for a searchable select: on focus, syncs the
 * query to the selected option's display name; on blur, restores it.
 *
 * @param params Selected value, query, resolver, and state setters/ref.
 * @returns `{ onFocus, onBlur }` handlers for the input element.
 */
export function useSelectFocus({
  selectedValue,
  query,
  getDisplayNameForValue,
  setQuery,
  setIsFocused,
  displayNameRef,
}: UseSelectFocusParams) {
  const onFocus = useCallback(() => {
    if (selectedValue) {
      const displayName = getDisplayNameForValue(selectedValue);
      if (query !== displayName) setQuery(displayName);
    }
    setIsFocused(true);
  }, [selectedValue, query, getDisplayNameForValue, setQuery, setIsFocused]);

  const onBlur = useCallback(() => {
    const resolved = resolveBlurQuery(displayNameRef, selectedValue, getDisplayNameForValue);
    if (resolved) setQuery(resolved);
    setIsFocused(false);
  }, [selectedValue, getDisplayNameForValue, setQuery, setIsFocused, displayNameRef]);

  return { onFocus, onBlur };
}
